import { imprintsWeekly } from "./imprints";
import { eraCostMult } from "./perks";
import { rivalActive } from "./market";
import { RIVAL_DECISIONS } from "./rivalmoves";
import { furnishRoom } from "./layout";
import { C, checkins, problems as problemList, type ProblemId } from "@bd/content";
import { BALANCE } from "./balance";
import { isQuarterStart, priceIndex, WEEKS_PER_YEAR, yearOf } from "./calendar";
import { compile, sizeOf } from "./design";
import type { PlayerSetup } from "./engine";
import { creditLimit, loanWeek, makeLoan, storageCost, taxDue } from "./production";
import { annexFx, expectedFirstYear, fanMarket, salesWeek } from "./sales";
import { COUSIN_HANDLERS } from "./cousins";
import type { Company, CompanyStats, Design, GameState, PendingDecision, PhaseId, PhaseWork, Project, Room, Staff } from "./state";
import { baseRent, grantXp, hasTrait, makeFounder, moraleTarget, refreshCandidates, teamMood, traitFx, workRate, xpNeeded, furnFx, gardenMorale, roomFx, ageOf, retireAge } from "./studio";
import { startingUnlocks, researchWeek } from "./unlocks";
import { clamp, log, money, newId, rng, digits } from "./util";
import { perkFx } from "./perks";
import { modValue } from "./mods";
import { dpow } from "./dmath";

export const COMPANY_COLORS = ["#E85848", "#22968C", "#DEA440", "#6A5AD8", "#3A9C5C", "#D8609A"];

export function emptyStats(): CompanyStats {
  return {
    released: 0, hits: 0, flops: 0, firstReleaseWeek: null, first8Week: null, officeWeek: null, studioWeek: null, hqWeek: null,
    firstEvergreenWeek: null, bankruptcies: 0, rescues: 0, decisions: 0, interestingDecisions: 0, problemsFixed: 0, peakCash: 0,
    awards: 0, combosFound: 0, eventsAnswered: 0, arcsDone: 0,
  };
}

export function starterRoom(state: GameState): Room {
  // the garage's design corner: room for three people at desks, a lamp, shelves and a plant
  const room: Room = { id: newId(state, "r"), type: "design", x: 1, y: 1, w: 5, h: 4, door: { x: 3, y: 5 }, builtWeek: 0, furniture: [] };
  furnishRoom(state, room);
  return room;
}

export function createCompany(state: GameState, setup: PlayerSetup): Company {
  const r = rng(state, `company:${setup.id}`);
  const co: Company = {
    id: newId(state, "c"), name: setup.company, player: setup.id, color: setup.color ?? COMPANY_COLORS[state.companies.length % COMPANY_COLORS.length],
    cash: state.settings.startCash, fans: 0, reputation: 30, research: 0, level: "garage", rooms: [starterRoom(state)], pets: [],
    staff: [makeFounder(state, r)], candidates: [], projects: [], products: [], loans: [], unlocked: startingUnlocks(), researched: [],
    notebook: {}, mastery: {}, pending: [], ledger: [], stats: emptyStats(), bankrupt: false, autopilot: false, printQueue: [], negWeeks: 0,
    mods: [], flags: [], achievements: [], arcs: [], nextHype: 0, creditBonus: 0, awardBoost: 0, licenceOffers: [],
    director: { nextWeek: 14 + r.int(0, 8), nextArcWeek: 3 * 52 + r.int(0, 104), fired: {}, tension: "calm", lastEventWeek: 0, startUnlocks: 0 },
  };
  co.director.startUnlocks = co.unlocked.filter((u) => !u.startsWith("p:")).length;
  refreshCandidates(state, co, r);
  state.companies.push(co);
  log(state, "company", `${co.name} opens in a garage with ${money(co.cash)} and one designer.`, co.id);
  return co;
}

export function company(state: GameState, id: string): Company {
  const co = state.companies.find((c) => c.id === id || c.player === id);
  if (!co) throw new Error(`no company ${id}`);
  return co;
}

// ------------------------------------------------------------------ projects

export function newPhaseWork(need: number): PhaseWork {
  return { need: Math.max(1, Math.round(need)), done: 0, quality: 0 };
}

export function projectWork(p: Project, week = p.startedWeek): Record<PhaseId, PhaseWork> {
  const d = p.design;
  const f = C.format[d.format];
  const c = compile(d);
  // games grow over the decades, and a bigger box is more work in every phase
  const ramp = BALANCE.firstYearsWork + (1 - BALANCE.firstYearsWork) * Math.min(1, week / 360);
  const era = (1 + BALANCE.workPerYear * (yearOf(week) - 1965)) * sizeOf(d).work * BALANCE.workScale * ramp;
  return {
    prototype: newPhaseWork(30 * f.work * (1 + 0.05 * (d.focus.depth + d.focus.replay)) * (1 + c.complexity * 0.6) * era),
    playtest: newPhaseWork(1),
    art: newPhaseWork((12 + 6 * d.focus.art + 2 * d.focus.immersion) * (d.format === "rpg_book" ? 1.4 : 1) * era),
    rulebook: newPhaseWork((6 + 14 * c.complexity + 4 * d.focus.immersion) * (f.writingHeavy ? 2.5 : 1) * era),
  };
}

/**
 * About how many weeks a design takes from today with a team of `team` people (default: everyone not already on
 * a project, at least one). A guide for the size picker, not a promise: playtests and fixes add to it.
 */
export function estimateWeeks(co: Company, d: Design, week: number, team?: number): number {
  const work = projectWork({ design: d, startedWeek: week } as Project, week);
  const free = co.staff.filter((s) => !s.projectId);
  const people = (free.length ? free : co.staff).slice(0, Math.max(1, team ?? free.length));
  const n = Math.max(1, people.length);
  const per = people.length ? people.reduce((t, s) => t + workRate(co, s, "prototype", false).work, 0) / people.length : 1;
  const weekly = Math.max(0.1, per * n * teamFactor(n));
  return Math.max(2, Math.round((work.prototype.need + work.art.need + work.rulebook.need) / weekly));
}

/** Bigger teams help less per head. */
export function teamFactor(n: number): number {
  return n <= 1 ? 1 : dpow(n, BALANCE.teamExp) / n;
}

function addWork(w: PhaseWork, amount: number, quality: number): void {
  if (amount <= 0 || w.done >= w.need) return;
  const take = Math.min(amount, w.need - w.done);
  w.quality = (w.quality * w.done + quality * take) / (w.done + take);
  w.done += take;
}

export function projectStaff(co: Company, p: Project): Staff[] {
  return co.staff.filter((s) => s.projectId === p.id);
}

/** Work still to do in a project's current phase (playtest and fixes count as a little). */
function workLeft(p: Project): number {
  if (p.phase === "prototype") return Math.max(0, p.work.prototype.need - p.work.prototype.done);
  if (p.phase === "art" || p.phase === "rulebook") return Math.max(0, p.work.art.need - p.work.art.done) + Math.max(0, p.work.rulebook.need - p.work.rulebook.done);
  return p.fixing.length ? 20 : 4;
}

/** How well a person suits a project's current phase. */
function phaseFit(s: Staff, p: Project): number {
  const ph = p.phase === "rulebook" ? "rulebook" : p.phase === "art" ? "art" : "prototype";
  if (ph === "art") return s.role === "artist" || s.role === "writer" || s.role === "sculptor" ? 1.6 : 1;
  return s.role === "designer" || s.role === "developer" || s.founder ? 1.6 : s.role === "playtest_lead" ? 1.2 : 1;
}

/**
 * Free people join the project that needs them most (work left per person, weighted by fit), so parallel
 * projects all move; a project nobody is on borrows one person from the biggest team.
 */
function autoAssign(co: Company): void {
  const active = co.projects.filter((p) => p.phase !== "ready");
  if (!active.length) return;
  // a project waiting on you (prototype done, nothing to fix) keeps one lead; the rest go where there's work
  if (active.some((p) => workLeft(p) > 4)) {
    for (const p of active) {
      if (p.phase !== "playtest" || p.fixing.length) continue;
      const t = co.staff.filter((s) => s.projectId === p.id);
      if (t.length <= 1) continue;
      const lead = [...t].sort((a, b) => Number(!!b.founder) - Number(!!a.founder) || b.level - a.level)[0];
      for (const s of t) if (s !== lead) {
        s.projectId = undefined;
        s.lentFrom = p.id;
      }
    }
  }
  // the project they were lent from is moving again: they go back to it
  for (const s of co.staff) {
    if (!s.lentFrom) continue;
    const home = active.find((p) => p.id === s.lentFrom);
    if (!home) s.lentFrom = undefined;
    else if (home.phase !== "playtest" || home.fixing.length) {
      s.projectId = home.id;
      s.lentFrom = undefined;
    }
  }
  const team = new Map<string, number>();
  for (const p of active) team.set(p.id, 0);
  for (const s of co.staff) if (s.projectId && team.has(s.projectId)) team.set(s.projectId, team.get(s.projectId)! + 1);
  for (const s of co.staff) {
    if (s.projectId && team.has(s.projectId)) continue;
    if (s.training) continue;
    let best = active[0];
    let bv = -Infinity;
    for (const p of active) {
      const v = ((workLeft(p) + 1) / (team.get(p.id)! + 1)) * phaseFit(s, p);
      if (v > bv) {
        bv = v;
        best = p;
      }
    }
    s.projectId = best.id;
    team.set(best.id, team.get(best.id)! + 1);
  }
  // an empty project borrows the best-suited person from the biggest team (if that team can spare one)
  for (const p of active) {
    if (team.get(p.id)! > 0) continue;
    const [bigId, big] = [...team.entries()].sort((a, b) => b[1] - a[1])[0];
    if (big < 2) continue;
    const donor = co.staff.filter((s) => s.projectId === bigId && !s.founder && !s.training).sort((a, b) => phaseFit(b, p) - phaseFit(a, p))[0];
    if (!donor) continue;
    donor.projectId = p.id;
    team.set(bigId, big - 1);
    team.set(p.id, 1);
  }
  // once a week, one person moves from the most comfortable team to the most stretched one
  // (a small studio stays focused: spreading three people thin only slows the first games)
  if (active.length > 1 && co.staff.length >= 6) {
    const load = (p: Project) => (workLeft(p) + 1) / Math.max(1, team.get(p.id)!);
    const sorted = [...active].sort((a, b) => load(a) - load(b));
    const easy = sorted[0];
    const hard = sorted[sorted.length - 1];
    if (team.get(easy.id)! >= 2 && load(hard) > load(easy) * 3 && workLeft(hard) > 4) {
      const mover = co.staff.filter((s) => s.projectId === easy.id && !s.founder && !s.training).sort((a, b) => phaseFit(b, hard) - phaseFit(a, hard))[0];
      if (mover) mover.projectId = hard.id;
    }
  }
}

function projectsWeek(state: GameState, co: Company): void {
  autoAssign(co);
  for (const p of co.projects) {
    crowdWeek(state, co, p);
    const team = projectStaff(co, p);
    // fixes in progress (developers speed them up)
    if (p.fixing.length) {
      const sys = team.reduce((s, x) => s + x.stats.systems * (x.role === "developer" || x.role === "playtest_lead" ? 1.4 : 0.6), 0);
      const speed = clamp(sys / 50, 0.3, 4);
      const f = p.fixing[0];
      f.weeksLeft -= speed;
      if (f.weeksLeft <= 0) {
        p.fixing.shift();
        if (!p.fixes.includes(f.problem)) p.fixes.push(f.problem);
        co.stats.problemsFixed += 1;
        log(state, "fix", `${p.design.name}: fixed ${C.problem[f.problem].name.toLowerCase()} (${C.problem[f.problem].fix.toLowerCase()}).`, co.id);
      }
    }
    // the most experienced person on the team leads it: +2% pace for every level above 5
    const lead = Math.max(0, ...team.map((s) => s.level));
    const tf = teamFactor(team.length) * (1 + Math.max(0, lead - 5) * 0.02);
    if (p.phase === "prototype" || p.phase === "art" || p.phase === "rulebook" || p.fixing.length) {
      p.team ??= {};
      for (const s of team) if (!s.training) p.team[s.id] = (p.team[s.id] ?? 0) + 1;
    }
    if (p.phase === "prototype") {
      for (const s of team) {
        const r = workRate(co, s, "prototype", p.crunch);
        addWork(p.work.prototype, r.work * tf, r.quality);
      }
      maybeCheckin(state, co, p, "prototype", p.work.prototype.done / Math.max(1, p.work.prototype.need), team);
      if (p.work.prototype.done >= p.work.prototype.need) {
        p.phase = "playtest";
        log(state, "phase", `${p.design.name}: the prototype is ready. Run a playtest, or move on to art and rules.`, co.id);
      }
    } else if (p.phase === "art" || p.phase === "rulebook") {
      // word gets around: fans who like the look of it order ahead
      p.expectFY ??= expectedFirstYear(state, co, p);
      if (p.hype > 0.05) p.preorders += Math.round(p.expectFY * p.hype * BALANCE.preorderRate);
      for (const s of team) {
        const ra = workRate(co, s, "art", p.crunch);
        const rw = workRate(co, s, "rulebook", p.crunch);
        const artLeft = p.work.art.done < p.work.art.need;
        const rulesLeft = p.work.rulebook.done < p.work.rulebook.need;
        const craft = p.design.parts.minis && p.design.parts.minis !== "minis_none" ? roomFx(co, "craftQuality") : 0;
        if (artLeft && (!rulesLeft || ra.work * (0.5 + ra.quality) >= rw.work * (0.5 + rw.quality))) addWork(p.work.art, ra.work * tf, Math.min(1, ra.quality + craft));
        else if (rulesLeft) addWork(p.work.rulebook, rw.work * tf, rw.quality);
      }
      maybeCheckin(state, co, p, "art", p.work.art.done / Math.max(1, p.work.art.need), team);
      // halfway through the art, the first cover sketch goes up on the wall: something to see during the long stretch
      if (!p.sketched && p.work.art.done >= p.work.art.need * 0.5 && team.length) {
        p.sketched = true;
        const artist = [...team].sort((a, b) => b.stats.art - a.stats.art)[0];
        const who = artist.founder ? "You pin" : `${artist.name.split(" ")[0]} pins`;
        const lines = ["It's starting to look like a real game.", "Everyone stops by to have a look.", "The team argues happily about the colours.", "Someone has already drawn a moustache on the spare copy."];
        log(state, "sketch", `${who} up the first cover sketch for ${p.design.name}. ${lines[(p.design.name.length + co.products.length) % lines.length]}`, co.id);
      }
      if (p.work.art.done >= p.work.art.need && p.work.rulebook.done >= p.work.rulebook.need && !p.fixing.length) {
        p.phase = "ready";
        log(state, "phase", `${p.design.name} is ready to print.`, co.id);
      }
    }
    // crunch burns people out
    for (const s of team) {
      s.crunch = p.crunch;
    }
  }
}

/**
 * Once per game, halfway through either its prototype or its art, the lead asks you to pick a direction. The question
 * fits the game (its format, audience, box size and era), and one you were asked recently rests for a while.
 */
function maybeCheckin(state: GameState, co: Company, p: Project, phase: "prototype" | "art", progress: number, team: Staff[]): void {
  // not while you're still learning: your first game runs without interruptions
  if (progress < 0.5 || progress >= 0.95 || (p.checkins ?? []).length || p.kind !== "base" || co.stats.released < 1) return;
  // which phase asks is decided per game, so it isn't always the same moment
  const r = rng(state, `checkin:${p.id}`);
  if (r.chance(0.5) !== (phase === "art")) {
    if (phase === "prototype") return; // the art phase will ask
  }
  p.checkins = [phase];
  const year = yearOf(state.week);
  const recent = new Set(co.recentCheckins ?? []);
  const fits = checkins.filter((c) => c.phase === phase && (!c.when
    || ((!c.when.formats || c.when.formats.includes(p.design.format))
      && (!c.when.audiences || c.when.audiences.includes(p.design.audience))
      && (!c.when.scopes || c.when.scopes.includes(p.design.scope ?? "standard"))
      && (!c.when.from || year >= c.when.from)
      && (!c.when.to || year <= c.when.to))))
    // "could it be quicker?" only makes sense for a game over 20 minutes
    .filter((c) => !c.text.includes("{minutes}") || p.design.playTime > 20);
  const fresh = fits.filter((c) => !recent.has(c.id));
  const pool = fresh.length ? fresh : fits;
  if (!pool.length) return;
  // the ones that fit this game specifically come up more
  const def = r.weighted(pool, (c) => (c.when ? 2 : 1));
  co.recentCheckins = [...(co.recentCheckins ?? []), def.id].slice(-12);
  const lead = [...team].sort((a, b) => b.level - a.level)[0];
  // "{lead} asks" reads wrong for you, the founder: then it's the team asking
  const who = lead && !lead.founder ? lead.name.split(" ")[0] : "Your team";
  const fill = (t: string) => t.replaceAll("{game}", p.design.name).replaceAll("{lead}", who).replaceAll("{minutes}", String(p.design.playTime));
  addDecision(state, co, {
    // if you don't answer, the team takes the option that adds the least work
    kind: "checkin", title: fill(def.title), text: fill(def.text), weeks: 3,
    defaultOption: [...def.options].sort((a, b) => ((a.fx.work ?? 0) + (a.fx.artWork ?? 0)) - ((b.fx.work ?? 0) + (b.fx.artWork ?? 0)))[0].id,
    options: def.options.map((o) => ({ id: o.id, label: o.label, detail: o.detail })), data: { projectId: p.id, checkin: def.id },
  });
}

/** A crowdfunding campaign runs four weeks: backers pledge, and when it ends you have their money and a promise to keep. */
function crowdWeek(state: GameState, co: Company, p: Project): void {
  const c = p.crowd;
  if (!c || c.done) return;
  p.expectFY ??= expectedFirstYear(state, co, p);
  const weekly = Math.round((p.expectFY * (BALANCE.crowdBase + p.hype * BALANCE.crowdPerHype)) / 4);
  c.backers += weekly;
  // a good campaign is news in itself
  p.hype = clamp(p.hype + 0.015);
  if (state.week >= c.endsWeek) {
    c.done = true;
    // short of the goal, nobody pays: the campaign fails and the buzz fades a little
    if (c.goal && c.backers < c.goal) {
      p.hype = clamp(p.hype - 0.05);
      log(state, "crowd", `${p.design.name}'s campaign falls short: ${digits(c.backers)} of the ${digits(c.goal)} backers it needed. Nobody is charged.`, co.id);
      return;
    }
    c.funded = true;
    const raised = Math.round(c.backers * p.design.price * 0.75 * (1 - BALANCE.crowdFee));
    c.raised = raised;
    co.cash += raised;
    p.preorders += c.backers;
    log(state, "crowd", `${p.design.name} is funded: ${digits(c.backers)} backers pledged ${money(raised)} after the platform's cut. They expect the box within ${Math.round((c.deadline - state.week) / 4.33)} months.`, co.id);
  }
}

export function queueFix(p: Project, problem: ProblemId): number {
  const def = C.problem[problem];
  if (p.fixes.includes(problem) || p.fixing.some((f) => f.problem === problem)) return 0;
  p.fixing.push({ problem, weeksLeft: def.fixWeeks });
  return def.fixWeeks;
}

// ------------------------------------------------------------------ people

function staffWeek(state: GameState, co: Company): void {
  const r = rng(state, `staff:${co.id}`);
  const recentHits = co.products.filter((p) => p.launched && p.score >= 8 && state.week - p.releaseWeek < 52).length;
  const leaving: Staff[] = [];
  const mood = teamMood(co, state.week);
  for (const s of co.staff) {
    const busy = !!s.projectId || !!s.training || (s.awayUntil ?? 0) > state.week;
    s.idleWeeks = busy ? 0 : (s.idleWeeks ?? 0) + 1;
    const target = moraleTarget(co, s, recentHits, mood);
    s.morale = clamp(s.morale + (target - s.morale) * 0.12, 0, 100);
    if (s.crunch) s.burnout = clamp(s.burnout + 4 * (1 + traitFx(s, "burnout") + perkFx(co, "burnout")), 0, 100);
    else s.burnout = clamp(s.burnout - 1.5, 0, 100);
    if (s.training) {
      s.training.weeksLeft -= 1;
      if (s.training.weeksLeft <= 0) {
        const gain = r.int(5, 9);
        s.stats[s.training.stat] = Math.min(99, s.stats[s.training.stat] + gain);
        log(state, "training", `${s.name} finished training: ${s.training.stat} +${gain}.`, co.id);
        s.training = undefined;
      }
    }
    if (s.xp >= xpNeeded(s.level)) if (grantXp(s, 0)) log(state, "levelup", `${s.name} reaches level ${s.level}.`, co.id);
    if (s.founder) continue;
    // reveal hidden traits after a few weeks on the job
    if (state.week - s.hiredWeek === 8) {
      for (const t of s.traits) if (!s.revealed.includes(t)) {
        s.revealed.push(t);
        log(state, "trait", `You've noticed ${s.name} is ${C.trait[t].name.toLowerCase()}: ${C.trait[t].desc}`, co.id);
      }
    }
    if (s.burnout > 85 && r.chance(0.06)) leaving.push(s);
    else if (s.morale < 25 && r.chance(0.035)) leaving.push(s);
  }
  for (const s of leaving) {
    co.staff = co.staff.filter((x) => x.id !== s.id);
    log(state, "quit", `${s.name} quits (${s.burnout > 85 ? "burned out" : "unhappy"}).`, co.id);
  }
}

function poachingQuarter(state: GameState, co: Company): void {
  const r = rng(state, `poach:${co.id}`);
  for (const s of co.staff) {
    if (s.founder || s.level < 3 || hasTrait(s, "loyal")) continue;
    const p = 0.05 * (1 - s.morale / 100) * 2 + (hasTrait(s, "ambitious") ? 0.03 : 0);
    if (!r.chance(p)) continue;
    const open = state.rivals.filter((x) => C.rival[x.id] && rivalActive(x, C.rival[x.id], state.week));
    if (!open.length) return;
    const rival = r.pick(open);
    const offer = Math.round(s.salary * r.float(1.25, 1.6));
    if (co.pending.some((d) => d.kind === "poach" && d.data?.staffId === s.id)) continue;
    addDecision(state, co, {
      kind: "poach", title: `${C.rival[rival.id].name} wants ${s.name}`,
      text: `${C.rival[rival.id].name} offers ${s.name} ${money(offer)} a week. Match it, or let them go?`,
      options: [{ id: "match", label: `Match: ${money(offer)}/wk` }, { id: "go", label: "Let them go" }], defaultOption: "go", weeks: 3,
      data: { staffId: s.id, rival: rival.id, offer },
    });
  }
}

// ------------------------------------------------------------------ decisions

export interface DecisionSpec {
  kind: string;
  title: string;
  text: string;
  options: PendingDecision["options"];
  defaultOption: string;
  weeks: number;
  data?: Record<string, any>;
}

export function addDecision(state: GameState, co: Company, spec: DecisionSpec): PendingDecision {
  const d: PendingDecision = {
    id: newId(state, "d"), kind: spec.kind, week: state.week, title: spec.title, text: spec.text, options: spec.options,
    defaultOption: spec.defaultOption, expiresWeek: state.week + spec.weeks, data: spec.data,
  };
  co.pending.push(d);
  return d;
}

export type DecisionHandler = (state: GameState, co: Company, d: PendingDecision, option: string) => void;
export const DECISION_HANDLERS: Record<string, DecisionHandler> = {
  checkin(state, co, d, option) {
    const p = co.projects.find((x) => x.id === d.data?.projectId);
    const opt = checkins.find((c) => c.id === d.data?.checkin)?.options.find((o) => o.id === option);
    if (!p || !opt) return;
    const fx = opt.fx;
    if (fx.quality) p.work.prototype.quality = clamp(p.work.prototype.quality + fx.quality);
    if (fx.artQuality) p.work.art.quality = clamp(p.work.art.quality + fx.artQuality);
    if (fx.hype) p.hype = clamp(p.hype + fx.hype);
    if (fx.replay) p.replayBonus = (p.replayBonus ?? 0) + fx.replay;
    if (fx.work) p.work.prototype.need = Math.max(p.work.prototype.done + 1, Math.round(p.work.prototype.need * (1 + fx.work)));
    if (fx.artWork) p.work.art.need = Math.max(p.work.art.done + 1, Math.round(p.work.art.need * (1 + fx.artWork)));
    log(state, "checkin", `${p.design.name}: ${opt.label.toLowerCase()} (${opt.detail.charAt(0).toLowerCase() + opt.detail.slice(1)}).`, co.id);
  },
  poach(state, co, d, option) {
    const s = co.staff.find((x) => x.id === d.data?.staffId);
    if (!s) return;
    if (option === "match") {
      s.salary = d.data!.offer;
      s.morale = clamp(s.morale + 15, 0, 100);
      log(state, "poach", `${s.name} stays for ${money(s.salary)} a week.`, co.id);
    } else {
      co.staff = co.staff.filter((x) => x.id !== s.id);
      const rs = state.rivals.find((x) => x.id === d.data?.rival);
      if (rs) rs.staffPoached += 1;
      log(state, "poach", `${s.name} leaves for ${C.rival[d.data!.rival].name}.`, co.id);
    }
  },
};

export function resolveDecision(state: GameState, co: Company, decisionId: string, option: string): boolean {
  const d = co.pending.find((x) => x.id === decisionId);
  if (!d || !d.options.some((o) => o.id === option)) return false;
  co.pending = co.pending.filter((x) => x.id !== decisionId);
  (DECISION_HANDLERS[d.kind] ?? RIVAL_DECISIONS[d.kind] ?? COUSIN_HANDLERS[d.kind])?.(state, co, d, option);
  return true;
}

function decisionsWeek(state: GameState, co: Company): void {
  for (const d of [...co.pending]) if (state.week >= d.expiresWeek) resolveDecision(state, co, d.id, d.defaultOption);
}

// ------------------------------------------------------------------ money

/** What one of your shops earns a week: more as your fan base grows. */
export function storeWeekly(co: Company, id: string, week: number): number {
  return (C.store[id]?.income ?? 0) * priceIndex(week) * (0.6 + 0.4 * Math.min(2, co.fans / (2.5 * fanMarket(week))));
}

function financeWeek(state: GameState, co: Company, income: number): void {
  const idx = priceIndex(state.week);
  const byCat: Record<string, number> = { sales: income };
  const salaries = co.staff.reduce((s, x) => s + x.salary, 0);
  const rent = Math.round(baseRent(co) * idx);
  const storage = storageCost(state, co).cost;
  let loans = 0;
  for (const l of co.loans) {
    // Gran's loan is paid back only when there's money to spare
    if (l.lender === "gran" && co.cash + income < 3000 * idx) continue;
    loans += loanWeek(l).paid;
  }
  co.loans = co.loans.filter((l) => l.principal > 0.5 && l.weeksLeft > -52);
  const roomIncome = Math.round(co.rooms.reduce((s, r) => s + (C.room[r.type]?.fx.income ?? 0), 0) * idx);
  // your shops in other towns earn more as your fan base grows
  const storeIncome = Math.round((co.stores ?? []).reduce((s, id) => s + storeWeekly(co, id, state.week), 0));
  const overheads = Math.round(salaries * Math.min(BALANCE.overheadCap, BALANCE.overheadPerPerson * co.staff.length));
  byCat.salaries = -salaries;
  byCat.rent = -rent;
  byCat.storage = -storage;
  byCat.loans = -loans;
  if (roomIncome) byCat.cafe = roomIncome;
  if (storeIncome) byCat.stores = storeIncome;
  if (overheads) byCat.overheads = -overheads;
  // royalties: deals from events and buyouts, plus what your imprints' new games earn
  const royalty = Math.round(modValue(co, "royalty") * idx + imprintsWeekly(state, co));
  if (royalty) byCat.royalties = royalty;
  const investor = Math.round(income * Math.min(0.5, modValue(co, "investor")));
  if (investor) byCat.investor = -investor;
  // the R&D budget: a share of sales turned into research points (dearer each era)
  const rd = Math.round(income * clamp(co.rdShare ?? 0, 0, BALANCE.rdMax));
  if (rd) {
    byCat.rd = -rd;
    co.research += rd / (BALANCE.rdPointCost * idx * eraCostMult(yearOf(state.week)));
    co.deductible = (co.deductible ?? 0) + rd;
  }
  // online: shares of cousins' sales and print slots coming in; shares of yours going out
  const cousinsIn = Math.round(co.shareIn ?? 0);
  const cousinsOut = Math.round(co.shareOut ?? 0);
  if (cousinsIn) byCat.cousins = cousinsIn;
  if (cousinsOut) byCat.partners = -cousinsOut;
  co.shareIn = 0;
  co.shareOut = 0;
  const costs = salaries + rent + storage + loans + investor + overheads + rd + cousinsOut;
  co.cash += income + roomIncome + storeIncome + royalty + cousinsIn - costs;
  co.ledger.push({ week: state.week, income: income + roomIncome + storeIncome + royalty + cousinsIn, costs, byCat });
  if (co.ledger.length > WEEKS_PER_YEAR) co.ledger.shift();
  co.stats.peakCash = Math.max(co.stats.peakCash, co.cash);
  // research trickle from designers
  for (const s of co.staff) if (s.role === "designer") co.research += 0.015 * (s.stats.creativity / 50);
  survival(state, co);
}

/** Early mistakes cost money, never the save. Later, a long overdraft means bankruptcy and a restart in the garage. */
function survival(state: GameState, co: Company): void {
  if (co.cash >= 0) {
    co.negWeeks = 0;
    return;
  }
  if (state.week < state.settings.safeUntilWeek) {
    const amount = Math.round(-co.cash + 2500 * priceIndex(state.week));
    // one loan from Gran that grows, not a pile of them
    const existing = co.loans.find((l) => l.lender === "gran");
    if (existing) {
      existing.principal += amount;
      existing.weeksLeft = Math.max(existing.weeksLeft, 104);
    } else co.loans.push(makeLoan(state, "gran", amount, 156, 0));
    co.cash += amount;
    co.stats.rescues += 1;
    const owed = co.loans.find((l) => l.lender === "gran")!.principal;
    const why = co.stats.rescues >= 2
      ? `"That's ${money(owed)} you owe me now, dear. Shops only take so many new games a year: fewer games, with more time on each, sell better. And print enough to sell."`
      : `"Everyone's first games are a bit wobbly, dear."`;
    log(state, "rescue", `Gran Okafor lends you ${money(amount)}, interest-free: ${why}`, co.id);
    return;
  }
  // the bank turns an overdraft into a loan while the company still has credit
  const limit = creditLimit(state, co);
  if (-co.cash <= limit) {
    const amount = Math.round(-co.cash + 1000 * priceIndex(state.week));
    const loan = makeLoan(state, "bank", Math.min(amount, limit), 2 * WEEKS_PER_YEAR, 0.12);
    co.loans.push(loan);
    co.cash += loan.principal;
    log(state, "overdraft", `${co.name} ran out of cash, so the bank lent ${money(loan.principal)} at 12%.`, co.id);
    if (co.cash >= 0) {
      co.negWeeks = 0;
      return;
    }
  }
  const neg = co.negWeeks + 1;
  co.negWeeks = neg;
  if (neg === 1) log(state, "overdraft", `${co.name} is out of cash and credit. Cut costs or sell stock within 12 weeks, or the bank closes the studio.`, co.id);
  if (neg >= 12) bankrupt(state, co);
}

export function bankrupt(state: GameState, co: Company): void {
  co.stats.bankruptcies += 1;
  log(state, "bankrupt", `${co.name} goes bankrupt. The bank sells the stock, the buildings and the shops, and lets the staff go; you start again in a garage with your notebook and your name.`, co.id);
  co.staff = co.staff.filter((s) => s.founder);
  for (const s of co.staff) s.projectId = undefined;
  co.projects = [];
  for (const p of co.products) {
    p.stock = 0;
    p.orders = [];
    p.retired = true;
  }
  co.loans = [];
  co.annexes = [];
  co.stores = [];
  co.researching = [];
  co.level = "garage";
  co.rooms = [starterRoom(state)];
  co.cash = Math.round(5000 * priceIndex(state.week));
  co.reputation = clamp(co.reputation * 0.6, 0, 100);
  co.fans = Math.round(co.fans * 0.5);
  co.negWeeks = 0;
  co.bankrupt = false;
}

// ------------------------------------------------------------------ the weekly system

export const companySystem = {
  name: "companies",
  week(state: GameState) {
    for (const co of state.companies) {
      decisionsWeek(state, co);
      projectsWeek(state, co);
      const income = salesWeek(state, co);
      staffWeek(state, co);
      if (isQuarterStart(state.week + 1)) {
        refreshCandidates(state, co, rng(state, `hire:${co.id}`));
        poachingQuarter(state, co);
      }
      financeWeek(state, co, income);
      if (state.week % WEEKS_PER_YEAR === 0 && state.week > 0) {
        // people retire in their sixties; the best name a protégé
        for (const s of [...co.staff]) {
          if (s.founder || ageOf(s, state.week) < retireAge(s)) continue;
          co.staff = co.staff.filter((x) => x.id !== s.id);
          const years = Math.round((state.week - s.hiredWeek) / 52);
          const heir = s.level >= 10 ? co.staff.filter((x) => x.role === s.role && !x.founder).sort((a, b) => a.level - b.level)[0] : undefined;
          if (heir) {
            heir.xp += xpNeeded(heir.level);
            const perk = (s.perks ?? []).find((pk) => !(heir.perks ?? []).includes(pk));
            if (perk) heir.perks = [...(heir.perks ?? []), perk];
          }
          for (const x of co.staff) x.morale = clamp(x.morale + 2, 0, 100);
          log(state, "retire", `${s.name} retires after ${years} year${years === 1 ? "" : "s"}${heir ? `, and hands their know-how to ${heir.name}` : ""}. The team throws a party.`, co.id);
        }
        // a landmark raises your standing every year
        const rep = annexFx(co, "reputation");
        if (rep) co.reputation = clamp(co.reputation + rep, 0, 100);
        // everyone's pay keeps up with prices
        const rise = priceIndex(state.week) / priceIndex(state.week - WEEKS_PER_YEAR);
        for (const s of co.staff) if (!s.founder) s.salary = Math.round(s.salary * rise);
        const tax = taxDue(co, state.week);
        co.deductible = 0;
        if (tax > 0) {
          co.cash -= tax;
          const last = co.ledger.at(-1);
          if (last) {
            last.costs += tax;
            last.byCat.tax = -tax;
          }
          log(state, "tax", `${co.name} pays ${money(tax)} tax on last year's profit.`, co.id);
        }
      }
      co.gardenMorale = gardenMorale(co, state.week);
      researchWeek(state, co);
      // the archive shows off your past hits: they keep winning you a few fans every week
      const legacy = roomFx(co, "legacy") + furnFx(co, "legacy");
      if (legacy > 0) co.fans += Math.round(legacy * Math.min(40, co.stats.hits) * 4);
    }
  },
};

export function problemName(id: ProblemId): string {
  return problemList.find((p) => p.id === id)?.name ?? id;
}

export function yearsIn(state: GameState): number {
  return yearOf(state.week) - 1965;
}
